import { DescriptionField, DescriptionList } from "@dash/description-list";
import { Button, Chip } from "@dash/form";
import { Card, Divider, Header4, Page, PageHeader, SectionHeader } from "@dash/page";
import { QuerySkeleton } from "@dash/skeleton";
import { useToast } from "@dash/toast";
import { useTheme } from "@emotion/react";
import { AccountDescriptionList } from "@megarax/loyalty-users-admin";
import React from "react";
import { MdClose } from "react-icons/md";
import { useQueryClient } from "react-query";
import { useParams } from "react-router-dom-v5-compat";

import { useClientManager } from "@megaron/react-clients";
import { Uuid, uuidSerializer } from "@megaron/uuid";

import { FulfillmentCard } from "./FulfillmentCard";
import { NewFulfillmentCard } from "./NewFulfillmentCard";

export const OrderPage = () => {
  const theme = useTheme();
  const orderUuid = uuidSerializer.deserialize(useParams<{ orderId: string }>().orderId).assertOk().value;

  const orderQuery = useClientManager("loyaltyOrders")
    .order()
    .useQuery({ uuid: orderUuid }, { enabled: orderUuid !== undefined });

  const accountQuery = useClientManager("auth")
    .account()
    .useQuery(
      { uuid: orderQuery.data?.userUuid ?? ("" as Uuid) },
      {
        enabled: orderQuery.data !== undefined,
      },
    );

  const queryClient = useQueryClient();
  const toast = useToast();

  const cancelOrderMutation = useClientManager("loyaltyOrders")
    .cancelOrder()
    .useMutation({
      onSuccess: async () => {
        await queryClient.invalidateQueries(orderQuery.key);
      },
      onError: (error) => {
        toast.error("Nie udało się anulować zamówienia", error);
      },
    });

  return (
    <Page css={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
      <PageHeader>{`Zamówienie ${orderUuid}`}</PageHeader>
      <QuerySkeleton query={orderQuery} height="200px">
        {(order) => (
          <DescriptionList>
            <DescriptionField label="Nagroda">{order.rewardId}</DescriptionField>
            <DescriptionField label="Data utworzenia">{order.orderedAt.toLocaleString()}</DescriptionField>
            <DescriptionField label="Użytkownik">{order.userUuid}</DescriptionField>
          </DescriptionList>
        )}
      </QuerySkeleton>
      <Divider />

      <QuerySkeleton query={accountQuery} height="200px">
        {(account) => (
          <Card variant="secondary">
            <Header4>Użytkownik {account.uuid}</Header4>

            <AccountDescriptionList account={account} />
          </Card>
        )}
      </QuerySkeleton>
      <SectionHeader
        isHr
        actions={
          orderQuery.data?.isCancelled ? (
            <Chip color={theme.colors.danger}>Anulowane</Chip>
          ) : (
            <Button
              onClick={() => cancelOrderMutation.mutate({ uuid: orderUuid })}
              isLoading={cancelOrderMutation.isLoading}
              variant="outline"
              color="danger"
              size="small"
              icon={<MdClose />}
              css={{ whiteSpace: "pre" }}
            >
              Anuluj zamówienie
            </Button>
          )
        }
      >
        Realizacja
      </SectionHeader>
      <QuerySkeleton query={orderQuery} height="200px">
        {(order) => (
          <div css={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}>
            {order.fulfillments.map((f) => (
              <FulfillmentCard
                fulfillment={f}
                isPro={["szpachelka-grawerowana-pro", "paca-grawerowana-pro"].includes(order.rewardId)}
              />
            ))}
            <NewFulfillmentCard orderUuid={orderUuid} />
          </div>
        )}
      </QuerySkeleton>
    </Page>
  );
};
